import { Alert, AlertIcon, Box, Card, CardBody, Text, VStack } from "@chakra-ui/react";
import { normalizeReportViewModel } from "./normalizeReportViewModel";
import UnifiedKeyValueGrid from "./UnifiedKeyValueGrid";
import UnifiedNumericalTable from "./UnifiedNumericalTable";
import UnifiedReportHeader from "./UnifiedReportHeader";
import UnifiedTextBlockSection from "./UnifiedTextBlockSection";

const isEnglishLanguage = (language) =>
  ["en", "english"].includes(String(language || "").toLowerCase());

const SectionCard = ({ children, title }) => (
  <Card shadow="sm" border="1px solid" borderColor="blue.100" borderRadius="2xl" overflow="hidden">
    <Box bg="blue.500" color="white" px={2} py={1}>
      <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold">
        {title}
      </Text>
    </Box>
    <CardBody p={{ base: 2, md: 2 }}>{children}</CardBody>
  </Card>
);

export default function GenericContent({
  aiAnalysis,
  isRTL,
  language,
  reportApiResponse,
  t,
}) {
  const viewModel = normalizeReportViewModel({ aiAnalysis, reportApiResponse });
  const isEnglish = isEnglishLanguage(language);

  if (viewModel.hasError) {
    return (
      <Alert status="warning" borderRadius="xl">
        <AlertIcon />
        {viewModel.errorMessage ||
          t(
            "reports.aiInvalidResponse",
            isEnglish
              ? "The AI response did not match the required JSON contract."
              : "لم يلتزم الذكاء الاصطناعي بعقد JSON المطلوب."
          )}
      </Alert>
    );
  }

  return (
    <VStack spacing={1} align="stretch">
      <Card shadow="sm" border="1px solid" borderColor="blue.100" borderRadius="2xl">
        <CardBody py={{ base: 1, md: 1 }}
        >
          <UnifiedReportHeader
            description={viewModel.reportDescription}
            isRTL={isRTL}
            reportMeta={viewModel.reportMeta}
            subtitle={viewModel.reportSubtitle}
            title={viewModel.reportTitle}
          />
        </CardBody>
      </Card>

      {viewModel.basicReportData.length ? (
        <SectionCard
          title={t(
            "reports.basicReportData",
            isEnglish ? "Basic Report Data" : "بيانات التقرير الأساسية"
          )}
        >
          <UnifiedKeyValueGrid items={viewModel.basicReportData} />
        </SectionCard>
      ) : null}

      {viewModel.numericalResults ? (
        <SectionCard
          title={t(
            "reports.numericalResults",
            isEnglish ? "Numerical Results" : "نتائج رقمية"
          )}
        >
          <UnifiedNumericalTable numericalResults={viewModel.numericalResults} />
        </SectionCard>
      ) : null}

      {viewModel.analysis.length ? (
        <SectionCard title={t("reports.analysis", isEnglish ? "Analysis" : "التحليل")}>
          <UnifiedTextBlockSection items={viewModel.analysis} />
        </SectionCard>
      ) : null}

      {viewModel.recommendationsAndAlerts.length ? (
        <SectionCard
          title={t(
            "reports.recommendationsAndAlerts",
            isEnglish ? "Recommendations & Alerts" : "التوصيات والتنبيهات"
          )}
        >
          <UnifiedTextBlockSection items={viewModel.recommendationsAndAlerts} />
        </SectionCard>
      ) : null}
    </VStack>
  );
}
